import classNames from 'classnames';
import type { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router';

import Emoji from '../Emoji/Emoji';

type CardSize = 'sm' | 'md' | 'lg';

interface TextCardProps {
  to?: string;
  text: string;
  icon?: ReactNode;
  unifiedIcon?: string;
  size?: CardSize;
  textAnimation?: boolean;
  description?: string;
  shine?: boolean;
  onClick?: () => void;
}

const TextCard: FC<TextCardProps> = ({
  to,
  text,
  icon,
  unifiedIcon,
  description,
  shine,
  size = 'lg',
  onClick,
}) => {
  const navigate = useNavigate();
  const textObject = text.split('-');

  const handleOnClick = () => {
    if (onClick) {
      onClick();
    }

    if (to) {
      navigate(to);
    }
  };

  return (
    <div
      className={classNames(
        'relative rounded-2xl cursor-pointer transition duration-200 px-4 shadow-lg shadow-base-300',
        'hover:shadow-md active:translate-y-[0.5px] active:shadow-xs',
        {
          'bg-linear-to-tl from-primary/10 dark:from-primary/35 from-10% via-base-100 via-50% to-base-100':
            shine,
          'bg-linear-to-tl from-base-300/70 dark:from-base-100/20 from-10% via-base-100 via-50% to-base-100':
            !shine,
          'flex flex-row items-center py-8': size === 'lg',
          'flex flex-col items-center justify-center p-4 gap-2': size === 'sm',
        },
      )}
      onClick={handleOnClick}
      style={{ transition: 'filter 0.2s' }}
    >
      {size === 'sm' && (
        <>
          {icon && <div>{icon}</div>}
          {unifiedIcon && (
            <div className="ml-auto text-6xl">
              <Emoji unified={unifiedIcon} />
            </div>
          )}
        </>
      )}

      <div className="flex flex-col gap-0">
        <h3
          className={classNames('flex flex-col gap-0 font-extrabold', {
            'text-4xl': size === 'lg',
            'text-lg': size === 'sm',
          })}
        >
          {textObject.map((key, index) => (
            <p key={index}>{key}</p>
          ))}
        </h3>
        {description && (
          <p
            className={classNames('text-base-content/35 line-clamp-1', {
              'text-xs mt-1': size === 'lg',
              'text-[0.6rem]': size === 'sm',
            })}
          >
            {description}
          </p>
        )}
      </div>

      {size === 'lg' && (icon || unifiedIcon) && (
        <>
          {icon && <div className="ml-auto text-6xl">{icon}</div>}
          {unifiedIcon && (
            <div className="ml-auto text-6xl">
              <Emoji unified={unifiedIcon} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TextCard;
